import { z } from 'zod'

import { ORDER_STATUSES } from '@/ducks/orders/order.constants'

export type OrderStatuses = typeof ORDER_STATUSES[keyof typeof ORDER_STATUSES]

const LocalizedStringSchema = z.object({
  en: z.string(),
  fr: z.string().optional(),
  ar: z.string().optional()
})

const UserSchema = z.object({
  username: z.string(),
  last_name: z.string(),
  first_name: z.string().optional(),
  avatar: z.union([z.string(), z.string().url()]).optional()
})

const SubPackSchema = z.object({
  price: z.number(),
  _id: z.string(),
  name: LocalizedStringSchema,
  visibility: z.number().optional(),
  is_deleted: z.number().optional()
})

const AddonSchema = z.object({
  price: z.number(),
  name: LocalizedStringSchema
})
export type IAddon = z.infer<typeof AddonSchema>

const PackSchema = z.object({
  t_name: LocalizedStringSchema,
  t_description: z.string(),
  t_type: z.string(),
  sub_pack: z.array(SubPackSchema)
})

export type IPack = z.infer<typeof PackSchema>

export const FoodSchema = z.object({
  name: LocalizedStringSchema,
  id: z.string(),
  price: z.number(),
  slug: z.string().optional(),
  quantity: z.number(),
  instruction: z.string(),
  addons: z.array(AddonSchema),
  main_category: LocalizedStringSchema.optional(),
  total: z.number(),
  barcode: z.string().nullish(),
  base_pack: z.array(PackSchema),
  type_pack: z.array(PackSchema)
})

export type IFood = z.infer<typeof FoodSchema>

const StatusSchema = z.object({
  key: z.string(),
  title: LocalizedStringSchema
})

const SupportSchema = z.object({
  code: z.string(),
  number: z.string()
})

export const ApiFetchOrderDetailsResponseSchema = z.object({
  user: UserSchema,
  isReady: z.boolean(),
  order_id: z.string(),
  foods: z.array(FoodSchema),
  status: StatusSchema,
  time_out: z.number(),
  order_time: z.number(),
  estimated_preparation_time: z.number(),
  time_left: z.number(),
  returned: z.boolean(),
  assigned: z.boolean(),
  isReturnedToStock: z.boolean(),
  support: SupportSchema.optional(),
  store_name: LocalizedStringSchema,
  driver_name:z.string(),
  remainingETA:z.number()
})

export type ApiFetchOrderDetailsResponse = z.infer<
  typeof ApiFetchOrderDetailsResponseSchema
>

const PaymentMethodSchema = z.object({
  paymentMethodDisplayName: z.string(),
  paymentMethodIcon: z.string().optional(),
  paymentMethodDisplayName_i18n: z.object({
    en: z.string(),
    fr: z.string(),
    ar: z.string()
  })
})

const BillingsSchema = z.object({
  subtotal: z.number(),
  discount: z.number(),
  service_charge: z.number(),
  tax_amount: z.number(),
  tax_value: z.number(),
  commission: z.number(),
  earnings: z.number(),
  paymentMethod: PaymentMethodSchema
})

export type IBillings = z.infer<typeof BillingsSchema>

export const ApiFetchOrderBillingsResponseSchema = z.object({
  billings: BillingsSchema,
  isReady: z.boolean().optional(),
  isEpay: z.boolean().optional(),
  status: StatusSchema
})

export type ApiFetchOrderBillingsResponse = z.infer<
  typeof ApiFetchOrderBillingsResponseSchema
>

export const ApiAcceptOrderSchema = z.union([
  z.object({
    status: z.literal('1'),
    message: z.literal('Successfully accepted')
  }),
  z.object({
    status: z.literal('0'),
    message: z.union([
      z.literal('store_id is required'),
      z.literal('orderId is required')
    ])
  }),
  z.object({
    status: z.literal('ORDER_ACCEPTED'),
    message: z.literal('This order is already accepted')
  }),
  z.object({
    status: z.literal('FIRST_ORDER'),
    message: z.literal('This order is not authorized yet')
  }),
  z.object({
    status: z.literal('ALREADY_REJECTED'),
    message: z.literal('You have already Denied this order')
  }),

  z.object({
    status: z.literal('DELETED_ORDER'),
    message: z.literal('Your time is exceeded to accept this order')
  }),
  z.object({
    status: z.literal('ORDER_USED_CANCELLED'),
    message: z.literal('Order is already canceled by user')
  })
])

export type ApiAcceptOrder = z.infer<typeof ApiAcceptOrderSchema>

const OrderSchema = z.object({
  status: StatusSchema,
  user: UserSchema,
  restaurant_payout: z.string(),
  order_id: z.string(),
  food_count: z.number(),
  order_time: z.number(),
  isReady: z.boolean(),
  assigned: z.boolean(),
  returned: z.boolean(),
  driver_name:z.string(),
  remainingETA:z.number(),
  support: SupportSchema

})

export const ApiSearchOrdersHistoryResponseSchema = z.object({
  orders: z.array(OrderSchema),
  orders_count: z.number().optional()
})

export type Order = z.infer<typeof OrderSchema>
export type ApiSearchOrdersHistoryResponse = z.infer<
  typeof ApiSearchOrdersHistoryResponseSchema
>

export const ApiFetchRejectionReasonsSchema = z.object({
  reasons_count: z.number(),
  rejection_reasons: z.array(LocalizedStringSchema)
})
export type ApiFetchRejectionReasons = z.infer<
  typeof ApiFetchRejectionReasonsSchema
>

export type IOrderDetails = ApiFetchOrderDetailsResponse
